import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface ReportSnapshot {
  id: string;
  userId: string;
  targetUserId: string;
  snapshotDate: string;
  weekStart: string;
  weekEnd: string;
  snapshotType: string;
  weekTasks: SnapshotTask[];
  dayTasks: SnapshotTask[];
  completedToday: SnapshotTask[];
  completedWeek: SnapshotTask[];
  inProgressToday: SnapshotTask[];
  inProgressWeek: SnapshotTask[];
  notes: string;
  createdAt: string;
}

export interface SnapshotTask {
  id: string;
  name: string;
  status: string;
  priority: string;
  timeEstimated: number | null;
  timeSpent?: number | null; // from legacy frozen snapshots or time entries
}

interface SnapshotRow {
  id: string;
  user_id: string;
  target_user_id: string;
  snapshot_date: string;
  week_start: string;
  week_end: string;
  snapshot_type: string;
  week_tasks: SnapshotTask[];
  day_tasks: SnapshotTask[];
  completed_today: SnapshotTask[];
  completed_week: SnapshotTask[];
  in_progress_today: SnapshotTask[];
  in_progress_week: SnapshotTask[];
  notes: string;
  created_at: string;
}

function convert(row: SnapshotRow): ReportSnapshot {
  return {
    id: row.id,
    userId: row.user_id,
    targetUserId: row.target_user_id,
    snapshotDate: row.snapshot_date,
    weekStart: row.week_start,
    weekEnd: row.week_end,
    snapshotType: row.snapshot_type,
    weekTasks: row.week_tasks || [],
    dayTasks: row.day_tasks || [],
    completedToday: row.completed_today || [],
    completedWeek: row.completed_week || [],
    inProgressToday: row.in_progress_today || [],
    inProgressWeek: row.in_progress_week || [],
    notes: row.notes || '',
    createdAt: row.created_at,
  };
}

/** Save a report snapshot */
export async function saveReportSnapshot(opts: {
  userId: string;
  targetUserId: string;
  snapshotDate: string;
  weekStart: string;
  weekEnd: string;
  snapshotType: string;
  weekTasks: SnapshotTask[];
  dayTasks: SnapshotTask[];
  completedToday: SnapshotTask[];
  completedWeek: SnapshotTask[];
  inProgressToday: SnapshotTask[];
  inProgressWeek: SnapshotTask[];
  notes?: string;
}): Promise<ReportSnapshot> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('report_snapshots')
      .insert({
        user_id: opts.userId,
        target_user_id: opts.targetUserId,
        snapshot_date: opts.snapshotDate,
        week_start: opts.weekStart,
        week_end: opts.weekEnd,
        snapshot_type: opts.snapshotType,
        week_tasks: opts.weekTasks,
        day_tasks: opts.dayTasks,
        completed_today: opts.completedToday,
        completed_week: opts.completedWeek,
        in_progress_today: opts.inProgressToday,
        in_progress_week: opts.inProgressWeek,
        notes: opts.notes || '',
      })
      .select()
      .single()
  );
  if (error || !data) throw error || new Error('Failed to save snapshot');
  return convert(data);
}

/** Fetch snapshots for a user, newest first */
export async function fetchReportSnapshots(
  userId: string,
  limit = 30
): Promise<ReportSnapshot[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('report_snapshots')
      .select('*')
      .eq('user_id', userId)
      .order('snapshot_date', { ascending: false })
      .limit(limit)
  );
  if (error) throw error;
  return (data || []).map(convert);
}

/** Check if a snapshot already exists for a given date, target, and type */
export async function getSnapshotForDate(
  userId: string,
  targetUserId: string,
  snapshotDate: string,
  snapshotType?: string
): Promise<ReportSnapshot | null> {
  let query = supabase
    .from('report_snapshots')
    .select('*')
    .eq('user_id', userId)
    .eq('target_user_id', targetUserId)
    .eq('snapshot_date', snapshotDate);
  if (snapshotType) query = query.eq('snapshot_type', snapshotType);
  const { data, error } = await withRetry(() => query.maybeSingle());
  if (error) throw error;
  return data ? convert(data) : null;
}

/** Fetch all snapshots for a given date and target (all column types) */
export async function getSnapshotsForDate(
  userId: string,
  targetUserId: string,
  snapshotDate: string
): Promise<ReportSnapshot[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('report_snapshots')
      .select('*')
      .eq('user_id', userId)
      .eq('target_user_id', targetUserId)
      .eq('snapshot_date', snapshotDate)
  );
  if (error) throw error;
  return (data || []).map(convert);
}

/** Delete a snapshot */
export async function deleteReportSnapshot(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('report_snapshots').delete().eq('id', id)
  );
  if (error) throw error;
}
